import { useEffect, useState } from 'react';

export const EXTENSION_VERSION_REQUEST = 'harbor-extension-version';

/** Chrome Web Store extension id — override with NEXT_PUBLIC_CHROME_EXTENSION_ID for unpacked dev builds. */
export const CHROME_EXTENSION_ID =
  process.env.NEXT_PUBLIC_CHROME_EXTENSION_ID?.trim() || 'lhlmnjnodhnhmaghecpmbedalmmopmkh';

export const EXTENSION_STORE_URL =
  'https://chromewebstore.google.com/detail/harbor/lhlmnjnodhnhmaghecpmbedalmmopmkh?hl=en-US&utm_source=ext_sidebar';
export const SAFARI_EXTENSION_STORE_URL = process.env.NEXT_PUBLIC_SAFARI_EXTENSION_STORE_URL || '';
export const FIREFOX_EXTENSION_STORE_URL = process.env.NEXT_PUBLIC_FIREFOX_EXTENSION_STORE_URL || '';

/** Fallback for Firefox when no add-ons.mozilla.org listing URL is configured. */
export const FIREFOX_EXTENSION_SETTINGS_HREF = '/settings/candidates?tab=4';

export const EXTENSION_WHY_USEFUL =
  "Add candidates to jobs or add contacts to clients right from LinkedIn or GitHub. Unlike other browser extensions where you're still stuck doing manual candidate updates and outreach, Harbor lets you do it all at once. Auto-source a candidate's email address within the browser extension with one click, update a candidate's status, and even kick off an email sequence without ever leaving the page you're on.";

const PING_INTERVAL_MS = 2000;
const UNINSTALL_CONFIRMATION_COUNT = 3;
const DOM_PING_EVENT = 'harbor-extension-ping';
const DOM_PONG_EVENT = 'harbor-extension-pong';
const DOM_PING_TIMEOUT_MS = 500;
const EXTENSION_VERSION_ATTRIBUTE = 'data-harbor-extension-version';

type SupportedBrowser = 'safari' | 'firefox' | 'other';
export type ExtensionState = {
  installed: boolean;
  version: string | null;
  browser: SupportedBrowser;
  storeUrl: string;
  installLabel: string;
  checkingInstall: boolean;
};

type ChromeRuntime = {
  sendMessage: (
    extensionId: string,
    message: unknown,
    callback: (response: { version?: string | null }) => void,
  ) => void;
  lastError?: { message?: string };
};

const listeners = new Set<(state: ExtensionState) => void>();
let cachedExtension: ExtensionState | null = null;
let pingIntervalId: ReturnType<typeof setInterval> | null = null;
let focusListenersAttached = false;
let consecutivePingFailures = 0;

const getBrowserInfo = (): Pick<ExtensionState, 'browser' | 'storeUrl' | 'installLabel'> => {
  const ua = navigator.userAgent;
  const isSafari = /Safari/i.test(ua) && !/Chrome|CriOS|Chromium|Android|Edg|OPR|FxiOS/i.test(ua);
  const isFirefox = /Firefox\//i.test(ua);
  const browser: SupportedBrowser = isSafari ? 'safari' : isFirefox ? 'firefox' : 'other';
  const safariStoreUrl = SAFARI_EXTENSION_STORE_URL.trim();
  const firefoxStoreUrl = FIREFOX_EXTENSION_STORE_URL.trim();

  const storeUrl =
    browser === 'safari' ? safariStoreUrl : browser === 'firefox' ? firefoxStoreUrl : EXTENSION_STORE_URL;
  const installLabel =
    browser === 'safari'
      ? 'Install the Safari extension'
      : browser === 'firefox'
        ? 'Install the Firefox extension'
        : 'Install the browser extension';

  return { browser, storeUrl, installLabel };
};

const getDefaultState = (): ExtensionState => ({
  installed: false,
  version: null,
  checkingInstall: false,
  ...getBrowserInfo(),
});

const notifyListeners = () => {
  if (!cachedExtension) {
    return;
  }

  listeners.forEach(listener => listener(cachedExtension!));
};

const getChromeRuntime = (): ChromeRuntime | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const runtime = (window as typeof window & { chrome?: { runtime?: ChromeRuntime } }).chrome?.runtime;
  return runtime?.sendMessage ? runtime : null;
};

const markExtensionInstalled = (version: string | null) => {
  consecutivePingFailures = 0;

  const wasInstalled = cachedExtension?.installed ?? false;
  const nextVersion = typeof version === 'string' ? version : version === null ? null : null;

  if (wasInstalled && cachedExtension?.version === nextVersion) {
    return;
  }

  cachedExtension = {
    ...(cachedExtension ?? getDefaultState()),
    installed: true,
    version: nextVersion,
    checkingInstall: false,
  };

  notifyListeners();
};

const markExtensionUninstalled = () => {
  if (!cachedExtension?.installed) {
    return;
  }

  consecutivePingFailures = 0;
  cachedExtension = {
    ...(cachedExtension ?? getDefaultState()),
    installed: false,
    version: null,
    checkingInstall: false,
  };

  notifyListeners();
};

let domPongListener: (() => void) | null = null;
let domPingTimeout: ReturnType<typeof setTimeout> | null = null;

const clearDomPing = () => {
  if (domPongListener) {
    window.removeEventListener(DOM_PONG_EVENT, domPongListener);
    domPongListener = null;
  }

  if (domPingTimeout !== null) {
    clearTimeout(domPingTimeout);
    domPingTimeout = null;
  }
};

/**
 * Firefox and Safari do not support Chrome's `externally_connectable` web-page messaging, so the
 * extension answers a DOM event instead. Chrome primarily detects via runtime messaging; the
 * DOM path still runs on both browsers and is the one that matters on Firefox and Safari.
 */
const pingHarborExtensionViaDomEvent = () => {
  if (typeof window === 'undefined') {
    return;
  }

  clearDomPing();

  domPongListener = () => {
    clearDomPing();
    const version = document.documentElement?.getAttribute(EXTENSION_VERSION_ATTRIBUTE) ?? null;
    markExtensionInstalled(version);
  };

  window.addEventListener(DOM_PONG_EVENT, domPongListener);
  domPingTimeout = setTimeout(clearDomPing, DOM_PING_TIMEOUT_MS);
  window.dispatchEvent(new Event(DOM_PING_EVENT));
};

const pingHarborExtension = () => {
  pingHarborExtensionViaDomEvent();

  const runtime = getChromeRuntime();
  if (!runtime) {
    return;
  }

  try {
    runtime.sendMessage(CHROME_EXTENSION_ID, { type: EXTENSION_VERSION_REQUEST }, response => {
      if (runtime.lastError || !response || typeof response !== 'object') {
        if (cachedExtension?.installed) {
          consecutivePingFailures += 1;

          if (consecutivePingFailures >= UNINSTALL_CONFIRMATION_COUNT) {
            markExtensionUninstalled();
          }
        }

        return;
      }

      const version = response.version;
      markExtensionInstalled(typeof version === 'string' ? version : version === null ? null : null);
    });
  } catch {
    // Extension not installed or origin not in externally_connectable.
  }
};

const startExtensionPingInterval = () => {
  if (pingIntervalId !== null) {
    return;
  }

  pingIntervalId = setInterval(() => {
    pingHarborExtension();
  }, PING_INTERVAL_MS);
};

const onFocusOrVisible = () => {
  pingHarborExtension();
};

const attachFocusListeners = () => {
  if (focusListenersAttached || typeof window === 'undefined') {
    return;
  }

  window.addEventListener('focus', onFocusOrVisible);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      onFocusOrVisible();
    }
  });
  focusListenersAttached = true;
};

const startExtensionDetection = () => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!cachedExtension) {
    cachedExtension = getDefaultState();
  }

  attachFocusListeners();
  pingHarborExtension();
  startExtensionPingInterval();
};

/** Call after the user opens the extension store so detection retries immediately. */
export function beginExtensionInstallCheck(): void {
  if (typeof window === 'undefined') {
    return;
  }

  startExtensionDetection();
  cachedExtension = {
    ...(cachedExtension ?? getDefaultState()),
    checkingInstall: true,
  };
  notifyListeners();
  pingHarborExtension();
}

export function useExtensionDetection(): ExtensionState {
  const [state, setState] = useState<ExtensionState>(cachedExtension ?? getDefaultState());

  useEffect(() => {
    startExtensionDetection();

    const listener = (nextState: ExtensionState) => {
      setState(nextState);
    };

    listeners.add(listener);

    if (cachedExtension) {
      setState(cachedExtension);
    }

    return () => {
      listeners.delete(listener);
    };
  }, []);

  return state;
}
